import { z } from 'zod'

export const oneItem = <T extends z.ZodType>(schema: T) =>
  z.object({
    items: z.tuple([schema])
  })

export const manyItems = <T extends z.ZodType>(schema: T) =>
  z.object({
    items: z.array(schema)
  })

export const manyItemsPaginatedByNum = <T extends z.ZodType>(schema: T) =>
  z.object({
    items: z.array(schema),
    next: z.number().optional(),
    limit: z.number()
  })

export const manyItemsPaginatedByKey = <T extends z.ZodType>(schema: T) =>
  z.object({
    items: z.array(schema),
    continueFrom: z.string().optional(),
    limit: z.number()
  })

export type JsonApi<T, U extends z.ZodType> = {
  pathFor: (params: T) => string
  responseSchema: U
}

export type BinaryApi<T> = {
  pathFor: (params: T) => string
}

export type PaginationQueryParams = z.infer<typeof paginationQueryParamsSchema>

export const paginationQueryParamsSchema = z.object({
  continueFrom: z.string().optional(),
  limit: z.number().optional()
})

/**
 * The original source of the data in a stream
 */
export enum DataSource {
  /**
   * Panorama's first love, the Gen3 20|20
   */
  Gen3 = 'prp_gen3_2020',
  /**
   * 20/20 SeedSense
   */
  Gen2 = 'prp_gen2_dat',
  /**
   * Used during upload processing, before the source is known,
   * and if no valid source is able to be determined.
   */
  Unknown = 'unknown',
  /**
   * Climate FieldView exported .dat file
   */
  ClimateDat = 'climate_dat',
  /**
   * JD Shapefile
   */
  JDShapefile = 'jd_shapefile',
  /**
   * ISOXML file
   */
  ISOXML = 'isoxml'
}

export const jsonSchema = z.string().transform((str, ctx) => {
  try {
    return JSON.parse(str)
  } catch {
    ctx.addIssue({ code: 'custom', message: 'Invalid JSON' })
    return z.NEVER
  }
})

export enum MeasureSystem {
  Metric = 'metric',
  Imperial = 'imperial'
}

export const EXT_MAP = {
  [DataSource.Gen3]: '.2020',
  [DataSource.Gen2]: '.dat',
  [DataSource.JDShapefile]: '.zip',
  [DataSource.ISOXML]: '.zip',
  [DataSource.Unknown]: '',
  [DataSource.ClimateDat]: ''
} as const satisfies Record<DataSource, '' | `.${string}`>
